<template>
  <v-app-bar
    v-if="header !== 'none'"
    id="header"
    data-cy="header"
    :app="headerSettings.sticky === undefined"
    :shrink-on-scroll="headerSettings.sticky === undefined"
    :height="headerHeight"
    :min-height="headerScrolledHeight"
    color="white"
    elevation="0"
    :class="{
      hasBorder: hasBorder,
      'mobile-app': isMobileApp
    }"
  >
    <Container class="d-flex align-self-start px-4 px-xl-0">
      <div class="d-flex align-center" data-cy="headerUlekareLogo">
        <Logo
          :link="logoLink"
          :height="logoMaxHeight"
          :external="headerSettings.isLogoExternal"
          :external-logo-target="headerSettings.externalLogoTarget"
          :is-clickable="
            !isMobileApp &&
            (headerSettings.logoIsClickable === undefined ||
              headerSettings.logoIsClickable === true)
          "
        />
        <template v-if="headerSettings.leftSectionContent">
          <v-divider vertical inset class="align-self-center mx-6 d-none d-sm-flex" />
          <span
            v-if="headerSettings.leftSectionContent.text"
            class="d-none d-sm-inline-block"
            v-html="headerSettings.leftSectionContent.text"
          />
          <v-img
            v-if="headerSettings.leftSectionContent.image"
            class="d-none d-sm-inline-block"
            :class="{
              'ml-2': headerSettings.leftSectionContent.text,
              'cursor-pointer': headerSettings.leftSectionContent.image.link
            }"
            :src="headerSettings.leftSectionContent.image.url"
            :alt="headerSettings.leftSectionContent.image.alt"
            :max-height="logoLeftMaxHeight"
            :max-width="logoLeftMaxWidth"
            position="left"
            contain
            @click="headerSettings.leftSectionContent.image.link ? logoRedirect() : null"
          />
        </template>
      </div>
      <v-spacer data-cy="headerSpacer" />
      <div
        v-if="
          nonTextButtons.length ||
          textButtons.length ||
          headerSettings.rightSectionContent ||
          headerSettings.languageSwitcher
        "
        class="align-center justify-end"
        :class="rightSectionDisplay"
      >
        <template v-if="headerSettings.rightSectionContent">
          <span
            v-if="headerSettings.rightSectionContent.text"
            class="right-section-text grey-darken-1--text"
            :class="{ 'mr-2': headerSettings.rightSectionContent.image }"
            v-html="headerSettings.rightSectionContent.text"
          />
          <v-img
            v-if="headerSettings.rightSectionContent.image"
            class="flex-grow-0 flex-shrink-1"
            :src="headerSettings.rightSectionContent.image.url"
            :alt="headerSettings.rightSectionContent.image.alt"
            :max-height="logoMaxHeight"
            :max-width="logoRightMaxWidth"
            position="center"
            contain
          />
        </template>
        <NavigationButton
          v-for="(item, index) in textButtons"
          :key="`text_${index}`"
          :item="item"
        />
        <NavigationButton
          v-for="(item, index) in nonTextButtons"
          :key="`nonText_${index}`"
          :item="item"
          class="ml-4"
          data-cy="headerLoginButton"
          :class="{ 'mr-4': index < nonTextButtons.length - 1 }"
        />
        <LanguageSwitcher
          v-if="headerSettings.languageSwitcher"
          class="ml-2"
          data-cy="headerLanguageSwitcher"
          :redirect-type="
            headerSettings.languageSwitcher.redirectType
              ? headerSettings.languageSwitcher.redirectType
              : undefined
          "
        />
      </div>
      <v-btn
        v-if="headerSettings.backToClientZone"
        fab
        class="d-flex d-lg-none align-self-center"
        @click="$router.back()"
      >
        <v-icon>$arrowBack</v-icon>
      </v-btn>
      <v-btn
        v-if="showMobile"
        id="header-menu-open"
        fab
        class="d-flex d-lg-none align-self-center"
        @click="dialog = true"
      >
        <v-icon>$ulMenu</v-icon>
      </v-btn>
    </Container>
    <v-dialog
      v-if="showMobile"
      v-model="dialog"
      fullscreen
      hide-overlay
      content-class="header-menu-content"
      transition="dialog-bottom-transition"
    >
      <v-card
        class="pb-5 justify-start"
        :class="{
          'mobile-app': isMobileApp
        }"
      >
        <v-card-title class="px-4">
          <Logo :link="logoLink" :height="logoMaxHeight" />
          <v-spacer />
          <CloseButton @click="dialog = false" />
        </v-card-title>
        <v-card-text class="d-flex flex-column align-center">
          <NavigationButton
            v-for="(item, index) in headerSettings.items"
            :key="index"
            :item="item"
            class="mt-4"
            @close="dialog = false"
          />

          <LanguageSwitcher
            v-if="headerSettings.languageSwitcher"
            class="mx-auto py-5 mt-3"
            :redirect-type="
              headerSettings.languageSwitcher.redirectType
                ? headerSettings.languageSwitcher.redirectType
                : undefined
            "
            @close="dialog = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-app-bar>
</template>

<script>
import { mapGetters } from 'vuex';
import Container from '@/components/Atoms/Container';
import Logo from '@/components/Atoms/Logo';
import NavigationButton from '@/components/Atoms/NavigationButton';
import LanguageSwitcher from '@/components/Atoms/LanguageSwitcher';
import CloseButton from '@/components/Atoms/CloseButton';

export default {
  name: 'UlHeader',
  components: { CloseButton, LanguageSwitcher, NavigationButton, Logo, Container },
  props: {
    hasBorder: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      dialog: null,
      headerHeight: 80,
      headerScrolledHeight: 70,
      logoRightMaxWidth: 207,
      logoMaxHeight: 28
    };
  },
  computed: {
    showMobile() {
      return (
        (this.headerSettings.showMobileHeader === undefined ||
          this.headerSettings.showMobileHeader) &&
        ((this.headerSettings.items && this.headerSettings.items.length) ||
          this.headerSettings.languageSwitcher)
      );
    },
    logoLink() {
      if (this.isUlHc) {
        return 'aeskuLab';
      }
      return this.headerSettings.logoLink || 'index';
    },
    logoLeftMaxWidth() {
      return !this.headerSettings.leftSectionContent?.image?.maxWidth ||
        this.headerSettings.leftSectionContent?.image?.maxWidth > 350
        ? 350
        : this.headerSettings.leftSectionContent.image.maxWidth;
    },
    logoLeftMaxHeight() {
      return !this.headerSettings.leftSectionContent?.image?.maxHeight ||
        this.headerSettings.leftSectionContent?.image?.maxHeight > 28
        ? 28
        : this.headerSettings.leftSectionContent.image.maxHeight;
    },
    textButtons() {
      return this.headerSettings.items?.filter(item => item.buttonProps?.text === undefined) ?? [];
    },
    nonTextButtons() {
      return this.headerSettings.items?.filter(item => item.buttonProps?.text === false) ?? [];
    },
    rightSectionDisplay() {
      if (
        this.headerSettings.showMobileHeader === false ||
        (this.headerSettings.rightSectionContent?.text &&
          !this.headerSettings.rightSectionContent?.image)
      ) {
        return 'd-flex';
      }

      let breakpoint = '';
      if (
        this.nonTextButtons.length ||
        this.textButtons.length ||
        this.headerSettings.languageSwitcher
      ) {
        breakpoint = 'lg';
      } else if (this.headerSettings.leftSectionContent) {
        breakpoint = 'md';
      } else if (
        this.headerSettings.rightSectionContent?.image &&
        this.headerSettings.rightSectionContent?.text
      ) {
        breakpoint = 'sm';
      }

      return `d-none d-${breakpoint}-flex`;
    },

    ...mapGetters({
      header: 'general/header',
      headerSettings: 'general/headerSettings',
      isScrolled: 'general/isScrolled'
    })
  },
  methods: {
    logoRedirect() {
      if (typeof this.headerSettings.leftSectionContent.image.clickAction === 'function') {
        this.headerSettings.leftSectionContent.image.clickAction();
      }

      this.newTab(this.headerSettings.leftSectionContent.image.link);
    }
  }
};
</script>

<style lang="scss" scoped>
#header {
  &.hasBorder {
    border-bottom: $border-width solid $grey-lighten !important;
  }

  .container {
    height: calc(100% - #{$border-width});

    > div {
      height: 100%;

      .v-divider {
        max-height: 32px;
        margin-top: -$border-width;
        border-width: 0 $border-width 0 0;
        border-color: $grey-lighten;
      }

      .right-section-text {
        font-size: $font-size-root;
      }
    }
  }
}

.mobile-app {
  padding-top: env(safe-area-inset-top);
}

.header-menu-content {
  .v-card__title {
    border-bottom: $border-width solid $grey-lighten;
    min-height: 70px;
  }
}
</style>
